import React, { useState, useEffect } from 'react';
import { Alert, Form, Table, Button, Card, FilterGrid } from '@/components/ui';
import CostBreakdownModal from '../components/CostBreakdownModal';
import ReportSummaryCard from '../components/ReportSummaryCard';
import { REPORT_HEADER_BG, reportHeaderCellStyle } from '../utils/reportTable';
import type { CostBreakdownTask } from '../../../services/reportService';
import ReportParametersCard from '../components/ReportParametersCard';
import { areasService, ServiceArea } from '../../../services/areasService';
import activityService from '../../../services/activityService';
import { Activity } from '../../../types/Activity';
import reportService from '../../../services/reportService';
import Toast from '../../../components/Toast';
import { pickCurrentPlanYear } from '../utils/quantifiedValueReport';
import { monthHeaderText, PLACEHOLDER_MONTH_HEADERS } from '../utils/monthHeader';

interface QBSActualDetailsReportViewProps {
  serviceAreas: ServiceArea[];
  loadingServiceAreas: boolean;
  defaultServiceAreaId?: string;
}

/** Red for a loss, dark green for a gain; null/blank/zero keeps the default text colour. */
const marginColor = (value: number | string): string | undefined => {
  const numeric = typeof value === 'number' ? value : value !== '' && value !== '-' ? parseFloat(value) : NaN;
  if (isNaN(numeric) || numeric === 0) return undefined;
  return numeric < 0 ? '#dc3545' : '#006400';
};


/** Trailing column headers: short label, full name in the tooltip, and the unit ($ or #) on every column. */
const SUMMARY_COLUMNS: { label: string; title: string; minWidth: string }[] = [
  { label: 'Value to date ($)', title: 'Value to date ($)', minWidth: '90px' },
  { label: 'Costs to date ($)', title: 'Costs to date ($)', minWidth: '90px' },
  { label: 'Gross Margin ($)', title: 'Gross Margin ($)', minWidth: '90px' },
  { label: 'GM %', title: 'Gross Margin %', minWidth: '70px' },
  { label: 'Accomplished (#)', title: 'Total Accomplishment (#)', minWidth: '90px' },
  { label: 'Units', title: 'Unit of measure', minWidth: '60px' },
  { label: 'Cost / Accomp. ($)', title: 'Cost per Accomplishment ($)', minWidth: '90px' },
  { label: 'Sched 2 Rate ($)', title: 'Schedule 2 Rate ($)', minWidth: '80px' },
  { label: 'Avg Value / Accomp. ($)', title: 'Average Actual Value per Accomplishment ($)', minWidth: '100px' },
  { label: 'GM / Accomp. ($)', title: 'Gross Margin per Accomplishment ($)', minWidth: '90px' }
];

interface ActivityRow {
  activity: Activity;
  /** Every activity id merged into this row, so the breakdown matches the displayed numbers. */
  activityIds: number[];
  type: string;
  monthValues: (number | string)[]; // M1-M12 values
  valueToDate: number | string;
  costsToDate: number | string;
  profit: number | string; // Gross Margin
  grossMarginPercent: number | string; // GM%
  totalAccomplishment: number | string;
  costPerAccomplish: number | string;
  sched2Rate: number | string;
  avgActualValuePerAccomplishment: number | string;
  grossMarginPerAccomplishment: number | string;
}

const QBSActualDetailsReportView: React.FC<QBSActualDetailsReportViewProps> = ({
  serviceAreas,
  loadingServiceAreas,
  defaultServiceAreaId
}) => {
  const [filters, setFilters] = useState({
    serviceArea: defaultServiceAreaId || '',
    reportingMonth: '', // Can stay empty
    quantifiedYear: '',
    startDate: '',
    endDate: ''
  });
  const [qbsPlanYearOptions, setQbsPlanYearOptions] = useState<number[]>([]);
  const [loadingQbsPlanYears, setLoadingQbsPlanYears] = useState(false);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [activityRows, setActivityRows] = useState<ActivityRow[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);
  const [monthHeaders, setMonthHeaders] = useState<Array<{ label: string; dateRange: string }>>([]);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [showToast, setShowToast] = useState(false);
  const [breakdown, setBreakdown] = useState<{
    show: boolean;
    title: string;
    tasks: CostBreakdownTask[];
    loading: boolean;
    error: string | null;
  }>({ show: false, title: '', tasks: [], loading: false, error: null });
  const [summary, setSummary] = useState<{
    totalValue: number;
    totalCosts: number;
    grossMargin: number;
    grossMarginPercent: number;
    taskCount: number;
  } | null>(null);

  // Update service area filter when defaultServiceAreaId changes
  useEffect(() => {
    if (defaultServiceAreaId && !filters.serviceArea) {
      setFilters(prev => ({ ...prev, serviceArea: defaultServiceAreaId }));
    }
  }, [defaultServiceAreaId]);

  useEffect(() => {
    if (!filters.serviceArea) {
      setQbsPlanYearOptions([]);
      setFilters((prev) => ({ ...prev, quantifiedYear: '' }));
      return;
    }
    let cancelled = false;
    const serviceAreaId = parseInt(filters.serviceArea, 10);
    if (Number.isNaN(serviceAreaId)) {
      setQbsPlanYearOptions([]);
      return;
    }
    setLoadingQbsPlanYears(true);
    areasService
      .getQuantifiedPlans(serviceAreaId)
      .then((plans) => {
        if (cancelled) return;
        const eligiblePlans = plans.filter((p) => p.status === 'Open' || p.status === 'Closed');
        const years = Array.from(
          new Set(
            eligiblePlans.map((p) => p.year)
          )
        ).sort((a, b) => b - a);
        setQbsPlanYearOptions(years);
        // Plan years are contract years (7, 8, 11...), not calendar years: default to the plan
        // whose window contains today, same as Quantified Value.
        const defaultYear = pickCurrentPlanYear(eligiblePlans);

        setFilters((prev) => ({
          ...prev,
          quantifiedYear:
            defaultYear != null ? String(defaultYear) : ''
        }));
      })
      .catch(() => {
        if (!cancelled) {
          setQbsPlanYearOptions([]);
          setFilters((prev) => ({ ...prev, quantifiedYear: '' }));
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingQbsPlanYears(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters.serviceArea]);

  // Fetch activities when filters change
  useEffect(() => {
    const fetchActivities = async () => {
      if (!filters.serviceArea || !filters.quantifiedYear) {
        setActivities([]);
        setActivityRows([]);
        return;
      }

      setLoadingActivities(true);
      try {
        // Fetch quantified activities filtered by service area and contract year from server
        const filtered = await activityService.getQuantifiedActivitiesByServiceAreaAndYear(
          Number(filters.serviceArea),
          Number(filters.quantifiedYear)
        );

        setActivities(filtered);

        // Create rows with placeholder data
        const rows: ActivityRow[] = filtered.map(activity => ({
          activity,
          activityIds: [activity.id],
          type: '', // Empty for now
          monthValues: Array(12).fill(''), // M1-M12 placeholder
          valueToDate: '',
          costsToDate: '',
          profit: '',
          grossMarginPercent: '',
          totalAccomplishment: '',
          costPerAccomplish: '',
          sched2Rate: activity.scheduled2Rate || '',
          avgActualValuePerAccomplishment: '',
          grossMarginPerAccomplishment: ''
        }));

        setActivityRows(rows);
      } catch (error) {
        console.error('Error fetching activities:', error);
        setErrorMessage('Failed to load activities. Please try again.');
        setActivities([]);
        setActivityRows([]);
      } finally {
        setLoadingActivities(false);
      }
    };

    fetchActivities();
  }, [filters.serviceArea, filters.quantifiedYear]);

  const handleGeneratePreview = async () => {
    if (!filters.serviceArea || !filters.quantifiedYear) {
      setErrorMessage('Please select Service Area and Quantified Year');
      return;
    }

    setLoadingPreview(true);
    setErrorMessage(null);
    try {
      const data = await reportService.getQBSActualDetails({
        serviceAreaId: parseInt(filters.serviceArea),
        quantifiedYear: parseInt(filters.quantifiedYear),
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined
      });
      
      setMonthHeaders(data.monthHeaders || []);
      setSummary(data.summary || null);
      
      // Map API response to ActivityRow format
      const rows: ActivityRow[] = data.activityRows.map(row => {
        // Find matching activity from activities list
        const activity = activities.find(a => a.id === row.activityId) || {
          id: row.activityId,
          activityNumber: row.activityNumber,
          activityName: row.activityName,
          unitOfMeasure: '',
          scheduled2Rate: row.scheduled2Rate
        } as Activity;
        
        return {
          activity,
          activityIds: row.activityIds?.length ? row.activityIds : [row.activityId],
          type: row.type || '',
          monthValues: row.monthValues,
          valueToDate: row.valueToDate,
          costsToDate: row.costsToDate,
          profit: row.profit,
          grossMarginPercent: row.grossMarginPercent || 0,
          totalAccomplishment: row.totalAccomplishment,
          costPerAccomplish: row.costPerAccomplishment,
          sched2Rate: row.scheduled2Rate,
          avgActualValuePerAccomplishment: row.avgActualValuePerAccomplishment,
          grossMarginPerAccomplishment: row.grossMarginPerAccomplishment || 0
        };
      });
      
      setActivityRows(rows);
    } catch (error: any) {
      console.error('Error generating QBS Actual Details report:', error);
      setErrorMessage(error?.response?.data?.message || 'Failed to generate QBS Actual Details report. Please try again later.');
      setActivityRows([]);
      setSummary(null);
    } finally {
      setLoadingPreview(false);
    }
  };


  const formatValue = (value: number | string, isDollarAmount: boolean = true): string => {
    if (value === '' || value === null || value === undefined) {
      return '-';
    }
    if (typeof value === 'number') {
      // Change 0 to '-'
      if (value === 0) {
        return '-';
      }
      if (isDollarAmount) {
        // Round dollar amounts to nearest dollar
        return Math.round(value).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
      }
      return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    // Handle string values that might be '0' or '0.00'
    const numValue = parseFloat(String(value));
    if (!isNaN(numValue) && numValue === 0) {
      return '-';
    }
    return String(value);
  };

  const openBreakdown = async (row: ActivityRow) => {
    if (!filters.serviceArea || !filters.quantifiedYear) return;
    const title = row.activity.activityNumber && row.activity.activityName
      ? `(#${row.activity.activityNumber}) ${row.activity.activityName}`
      : row.activity.activityName || row.activity.activityNumber || 'Activity';
    setBreakdown({ show: true, title, tasks: [], loading: true, error: null });
    try {
      const tasks = await reportService.getQBSActualDetailsCostBreakdown({
        serviceAreaId: parseInt(filters.serviceArea),
        quantifiedYear: parseInt(filters.quantifiedYear),
        activityIds: row.activityIds,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined
      });
      setBreakdown((prev) => ({ ...prev, tasks, loading: false }));
    } catch (err: any) {
      setBreakdown((prev) => ({
        ...prev,
        loading: false,
        error: err?.response?.data?.message || 'Failed to load the cost breakdown. Please try again.'
      }));
    }
  };

  const formatSummaryDollars = (value: number): string =>
    (value < 0 ? '-$' : '$') + Math.abs(Math.round(value)).toLocaleString('en-US');

  const losingActivities = activityRows.filter((row) => typeof row.profit === 'number' && row.profit < 0).length;

  const truncateActivityName = (text: string, maxLength: number = 50): string => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const showSuccessToast = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
  };

  const handleExport = async () => {
    if (!filters.serviceArea || !filters.quantifiedYear) {
      setErrorMessage('Please select Service Area and Quantified Year');
      return;
    }

    setLoadingExport(true);
    setErrorMessage(null);
    try {
      await reportService.downloadQBSActualDetails({
        serviceAreaId: parseInt(filters.serviceArea),
        quantifiedYear: parseInt(filters.quantifiedYear),
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined
      });
      showSuccessToast('QBS Actual Details report exported successfully!');
    } catch (error: any) {
      console.error('Error exporting QBS Actual Details report:', error);
      setErrorMessage(error?.response?.data?.message || 'Failed to export QBS Actual Details report. Please try again later.');
    } finally {
      setLoadingExport(false);
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden'
    }}>
      {/* Header Section - Fixed */}
      <div style={{ flexShrink: 0 }}>
        {errorMessage && <Alert variant="danger" className="tw-mb-3">{errorMessage}</Alert>}
        
        <ReportParametersCard
          onGenerate={handleGeneratePreview}
          onExport={() => {}}
          loadingGenerate={loadingPreview}
          loadingExport={false}
          disableExport={true}
          generateButtonText="Run Report"
          hideExportButton={true}
          defaultCollapsed={false}
          onCollapseChange={setIsFiltersCollapsed}
        >
          <FilterGrid>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Service Area</Form.Label>
              <Form.Select
                size="sm"
                value={filters.serviceArea}
                onChange={(e) => setFilters(prev => ({ ...prev, serviceArea: e.target.value }))}
                disabled={loadingServiceAreas}
              >
                <option value="">All Service Areas</option>
                {serviceAreas.map((area) => (
                  <option key={area.id} value={area.id.toString()}>
                    {area.name || area.code || 'Unknown'}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Start Date</Form.Label>
              <Form.Control
                size="sm"
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">End Date</Form.Label>
              <Form.Control
                size="sm"
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Quantified Year</Form.Label>
              <Form.Select
                size="sm"
                value={filters.quantifiedYear}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    quantifiedYear: e.target.value
                  }))
                }
                disabled={
                  !filters.serviceArea ||
                  loadingQbsPlanYears ||
                  qbsPlanYearOptions.length === 0
                }
              >
                <option value="">
                  {!filters.serviceArea
                    ? 'Select service area first'
                    : loadingQbsPlanYears
                      ? 'Loading years…'
                      : qbsPlanYearOptions.length === 0
                        ? 'No quantified plans'
                        : 'Select year'}
                </option>
                {qbsPlanYearOptions.map((y) => (
                  <option key={y} value={String(y)}>
                    {y}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </FilterGrid>
        </ReportParametersCard>

        {summary && (
          <ReportSummaryCard
            title="Summary"
            icon="bi-cash-coin"
            tooltipId="qc-summary-tooltip"
            tooltip={
              <>
                Totals count each task once. A task linked to several activities appears on each of
                those rows, so adding up the rows can give a higher number.
              </>
            }
            items={[
              { label: 'Value to date ($)', value: formatSummaryDollars(summary.totalValue) },
              { label: 'Costs to date ($)', value: formatSummaryDollars(summary.totalCosts) },
              { label: 'Gross Margin ($)', value: formatSummaryDollars(summary.grossMargin), color: marginColor(summary.grossMargin) },
              {
                label: 'GM %',
                value: summary.totalValue > 0 ? `${summary.grossMarginPercent.toFixed(1)}%` : '—',
                color: marginColor(summary.grossMarginPercent)
              },
              { label: 'Tasks (#)', value: summary.taskCount.toLocaleString('en-US') },
              {
                label: 'Activities at a loss (#)',
                value: losingActivities,
                color: losingActivities > 0 ? '#dc3545' : undefined
              }
            ]}
          />
        )}
      </div>

      {/* Report Data Section - fills the remaining pane height */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0
      }}>
        <Card style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          minWidth: 0
        }}>
          <Card.Header className="tw-flex tw-justify-between tw-items-center" style={{ flexShrink: 0 }}>
            <h6 className="tw-mb-0">
              Quantified Costing
              {activities.length > 0 && (
                <span className="tw-text-content-muted tw-ml-2" style={{ fontSize: '0.875rem', fontWeight: 'normal' }}>
                  ({activities.length} {activities.length === 1 ? 'activity' : 'activities'})
                </span>
              )}
            </h6>
            <div className="tw-flex tw-items-center tw-gap-2">
              {loadingActivities && (
                <span className="tw-text-content-muted" style={{ fontSize: '0.875rem' }}>
                  Loading activities...
                </span>
              )}
              {activityRows.length > 0 && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleExport}
                  disabled={!filters.serviceArea || !filters.quantifiedYear || loadingExport}
                >
                  <i className="bi bi-download tw-mr-1"></i>
                  {loadingExport ? 'Exporting...' : 'Export Report'}
                </Button>
              )}
            </div>
          </Card.Header>

          {/* Table Container - a single scroll region sized purely by flex (no viewport-relative
              maxHeight - that's what made the horizontal scrollbar land off-screen; see
              ReportPreviewTable.tsx for the same fix on the shared table). */}
          <Card.Body className="tw-p-0" style={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              overflow: 'auto',
              minHeight: 0,
              minWidth: 0,
              height: '100%'
            }}>
              {activityRows.length === 0 && !loadingActivities ? (
                <div className="tw-text-center tw-py-5 tw-text-content-muted">
                  {!filters.serviceArea || !filters.quantifiedYear 
                    ? 'Please select Service Area and Quantified Year to view activities'
                    : 'No activities found for the selected filters'}
                </div>
              ) : (
                <Table striped bordered hover style={{ marginBottom: 0, width: 'max-content', minWidth: '100%' }}>
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10 }}>
                    <tr>
                      <th style={{ 
                        maxWidth: '20%',
                        width: '20%',
                        backgroundColor: REPORT_HEADER_BG,
                        position: 'sticky',
                        left: 0,
                        zIndex: 12,
                        boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                      }} title="Activity">Activity</th>
                      {(monthHeaders.length > 0 ? monthHeaders : PLACEHOLDER_MONTH_HEADERS).map((month, i) => (
                        <th
                          key={month.label}
                          style={reportHeaderCellStyle('80px')}
                          title={`${monthHeaderText(month, i)} ($)`}
                        >
                          {`${monthHeaderText(month, i)} ($)`}
                        </th>
                      ))}
                      {SUMMARY_COLUMNS.map((column) => (
                        <th key={column.label} style={reportHeaderCellStyle(column.minWidth)} title={column.title}>
                          {column.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {activityRows.map((row, index) => (
                      <tr key={row.activity.id || index}>
                        <td style={{ 
                          padding: '8px 12px',
                          position: 'sticky',
                          left: 0,
                          zIndex: 9,
                          backgroundColor: 'white',
                          boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                          maxWidth: '20%',
                          width: '20%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {truncateActivityName(
                            row.activity.activityNumber && row.activity.activityName
                              ? `(#${row.activity.activityNumber}) ${row.activity.activityName}`
                              : row.activity.activityName || row.activity.activityNumber || 'Unknown Activity'
                          )}
                        </td>
                        {row.monthValues.map((value, monthIndex) => (
                          <td
                            key={monthIndex}
                            style={{
                              textAlign: 'right',
                              padding: '8px 12px'
                            }}
                          >
                            {formatValue(value)}
                          </td>
                        ))}
                        <td style={{ textAlign: 'right', padding: '8px 12px' }}>
                          {formatValue(row.valueToDate)}
                        </td>
                        {/* Click the cost to see which tasks (and which cost types) make it up */}
                        <td
                          style={{
                            textAlign: 'right',
                            padding: '8px 12px',
                            ...(typeof row.costsToDate === 'number' && row.costsToDate !== 0
                              ? { cursor: 'pointer', color: '#0d6efd', textDecoration: 'underline' }
                              : {})
                          }}
                          title={typeof row.costsToDate === 'number' && row.costsToDate !== 0 ? 'Click to see the cost breakdown by task' : undefined}
                          onClick={typeof row.costsToDate === 'number' && row.costsToDate !== 0 ? () => openBreakdown(row) : undefined}
                        >
                          {formatValue(row.costsToDate)}
                        </td>
                        <td 
                          style={{ 
                            textAlign: 'right', 
                            padding: '8px 12px',
                            color: marginColor(row.profit)
                          }}
                        >
                          {formatValue(row.profit)}
                        </td>
                        <td 
                          style={{ 
                            textAlign: 'right', 
                            padding: '8px 12px',
                            color: marginColor(row.grossMarginPercent)
                          }}
                        >
                          {typeof row.grossMarginPercent === 'number' 
                            ? `${row.grossMarginPercent.toFixed(2)}%`
                            : (() => {
                                const numValue = typeof row.grossMarginPercent === 'number' 
                                  ? row.grossMarginPercent 
                                  : parseFloat(String(row.grossMarginPercent));
                                if (isNaN(numValue)) return '-';
                                return `${numValue.toFixed(2)}%`;
                              })()}
                        </td>
                        <td style={{ textAlign: 'right', padding: '8px 12px' }}>
                          {formatValue(row.totalAccomplishment, false)}
                        </td>
                        <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                          {row.activity.unitOfMeasure || '-'}
                        </td>
                        <td style={{ textAlign: 'right', padding: '8px 12px' }}>
                          {formatValue(row.costPerAccomplish)}
                        </td>
                        <td style={{ textAlign: 'right', padding: '8px 12px' }}>
                          {formatValue(row.sched2Rate)}
                        </td>
                        <td style={{ textAlign: 'right', padding: '8px 12px' }}>
                          {formatValue(row.avgActualValuePerAccomplishment)}
                        </td>
                        <td style={{ textAlign: 'right', padding: '8px 12px', color: marginColor(row.grossMarginPerAccomplishment) }}>
                          {formatValue(row.grossMarginPerAccomplishment)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </Card.Body>
        </Card>
      </div>

      <CostBreakdownModal
        show={breakdown.show}
        onHide={() => setBreakdown((prev) => ({ ...prev, show: false }))}
        title={breakdown.title}
        tasks={breakdown.tasks}
        loading={breakdown.loading}
        error={breakdown.error}
      />

      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        type="success"
        show={showToast}
        onClose={() => setShowToast(false)}
      />
    </div>
  );
};

export default QBSActualDetailsReportView;